import { App, Skeleton } from 'antd';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { extractErrorMessage, httpClient } from '@shared/api';

interface Bot {
  platform: 'max' | 'telegram';
  username: string;
  title: string | null;
  link: string;
}

export interface Setup {
  bot: Bot | null; // MAX
  tg_bot: Bot | null; // Telegram
  operators: Array<{
    id: string;
    name: string;
    status: string;
    linked: boolean;
    linked_max: boolean;
    linked_tg: boolean;
  }>;
  assistants: Array<{ id: string; name: string; enabled: boolean }>;
}

type Platform = 'max' | 'telegram';

const BOT_HELP: Record<Platform, { label: string; how: string }> = {
  max: { label: 'MAX', how: 'Создайте бота на dev.max.ru и вставьте его токен.' },
  telegram: {
    label: 'Telegram',
    how: 'Создайте бота у @BotFather (команда /newbot) и вставьте его токен. Нужен отдельный бот — не тот, что подключён к ассистенту.',
  },
};

export const SETUP_KEY = ['operators-setup'];
const KEY = SETUP_KEY;

/**
 * Серия O: «Операторы». Боты для операторов (MAX и/или Telegram — запрос приходит
 * во все сразу), операторы с привязкой кодом,
 * у каких ассистентов клиенты могут позвать человека. Минималистично (решение 29.10).
 */
export function OperatorsSetup() {
  const { message, modal } = App.useApp();
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery<Setup>({
    queryKey: KEY,
    queryFn: async () => (await httpClient.get<Setup>('/operators/setup')).data,
  });
  const refresh = () => qc.invalidateQueries({ queryKey: KEY });

  const [token, setToken] = useState<Record<Platform, string>>({ max: '', telegram: '' });
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState<{
    id: string;
    value: string;
    max: boolean;
    tg: boolean;
  } | null>(null);

  // Пока показан код — раз в 2 с проверяем, привязался ли оператор.
  useEffect(() => {
    if (!code) return;
    const t = window.setInterval(async () => {
      const d = (await httpClient.get<Setup>('/operators/setup')).data;
      qc.setQueryData(KEY, d);
      // Привязался в каком-то из ботов — сравниваем с тем, что было до кода.
      const op = d.operators.find((o) => o.id === code.id);
      if (op && (op.linked_max !== code.max || op.linked_tg !== code.tg)) setCode(null);
    }, 2000);
    const stop = window.setTimeout(() => setCode(null), 15 * 60 * 1000);
    return () => {
      window.clearInterval(t);
      window.clearTimeout(stop);
    };
  }, [code, qc]);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
      await refresh();
    } catch (e) {
      message.error(extractErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (isLoading) return <Skeleton active style={{ padding: 24 }} />;
  if (error || !data)
    return <div className="v2-content">Не удалось загрузить: {extractErrorMessage(error)}</div>;

  const bots = [data.bot, data.tg_bot].filter(Boolean) as Bot[];
  const anyBot = bots.length > 0;

  const linkOp = (id: string) =>
    run(async () => {
      const r = (await httpClient.post<{ code: string }>(`/operators/${id}/code`)).data;
      const op = data.operators.find((o) => o.id === id);
      setCode({ id, value: r.code, max: !!op?.linked_max, tg: !!op?.linked_tg });
    });

  const botRow = (platform: Platform, bot: Bot | null) => {
    const { label, how } = BOT_HELP[platform];
    if (bot) {
      return (
        <div className="op-row" key={platform}>
          <span>
            <span className="v2-muted v2-small">{label} · </span>
            <a href={bot.link} target="_blank" rel="noopener noreferrer" className="v2-link">
              @{bot.username} ↗
            </a>
          </span>
          <button
            type="button"
            className="v2-btn"
            disabled={busy}
            onClick={() =>
              modal.confirm({
                title: `Отключить бота в ${label}?`,
                content: `Запросы перестанут приходить в ${label}, в кабинете они останутся.`,
                okText: 'Отключить',
                cancelText: 'Отмена',
                onOk: () =>
                  run(() => httpClient.delete('/operators/bot', { params: { platform } })),
              })
            }
          >
            Отключить
          </button>
        </div>
      );
    }
    return (
      <div className="op-bot-add" key={platform}>
        <div className="v2-small">
          <b>{label}</b> <span className="v2-muted">— {how}</span>
        </div>
        <div className="op-row">
          <label className="v2-sr" htmlFor={`op-token-${platform}`}>
            Токен бота {label}
          </label>
          <input
            id={`op-token-${platform}`}
            className="st-input"
            placeholder={`Токен бота ${label}`}
            value={token[platform]}
            onChange={(e) => setToken((t) => ({ ...t, [platform]: e.target.value }))}
            autoComplete="off"
          />
          <button
            type="button"
            className="v2-btn is-primary"
            disabled={busy || token[platform].trim().length < 10}
            onClick={() =>
              run(async () => {
                await httpClient.post('/operators/bot', {
                  token: token[platform].trim(),
                  platform,
                });
                setToken((t) => ({ ...t, [platform]: '' }));
              })
            }
          >
            Подключить
          </button>
        </div>
      </div>
    );
  };

  const linkState = (o: Setup['operators'][number]) => {
    const parts: string[] = [];
    if (data.bot) parts.push(o.linked_max ? 'MAX ✓' : 'MAX не привязан');
    if (data.tg_bot) parts.push(o.linked_tg ? 'Telegram ✓' : 'Telegram не привязан');
    return parts.length ? ` · ${parts.join(' · ')}` : '';
  };

  return (
    <>
      <div className="op-wrap">
        <section className="v2-card op-card">
          <h2 className="v2-h2">Боты для уведомлений</h2>
          <p className="op-hint">
            Запросы клиентов приходят сюда, в кабинет. Чтобы получать их и на телефон, подключите
            своего бота в MAX, в Telegram или в обоих — запрос придёт во все сразу.
          </p>
          {botRow('max', data.bot)}
          {botRow('telegram', data.tg_bot)}
        </section>

        <section className="v2-card op-card">
          <h2 className="v2-h2">Операторы</h2>
          {data.operators.length === 0 && (
            <p className="op-hint">Добавьте себя или сотрудника — имя увидит клиент.</p>
          )}
          {data.operators.map((o) => (
            <div key={o.id} className="op-item">
              <div className="op-row">
                <span>
                  <b>{o.name}</b>{' '}
                  <span className="v2-small v2-muted">
                    {o.status === 'online' ? '· на смене' : '· не на смене'}
                    {linkState(o)}
                  </span>
                </span>
                <span className="op-actions">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={o.status === 'online'}
                    aria-label={`${o.name} на смене`}
                    title="На смене — получает запросы"
                    disabled={busy}
                    className={o.status === 'online' ? 'st-switch is-on' : 'st-switch'}
                    onClick={() =>
                      run(() =>
                        httpClient.put(`/operators/${o.id}/status`, {
                          online: o.status !== 'online',
                        }),
                      )
                    }
                  >
                    <span />
                  </button>
                  {anyBot && (
                    <button
                      type="button"
                      className="v2-btn"
                      disabled={busy}
                      onClick={() => linkOp(o.id)}
                    >
                      {o.linked ? 'Перепривязать' : 'Привязать'}
                    </button>
                  )}
                  <button
                    type="button"
                    className="v2-btn"
                    disabled={busy}
                    onClick={() => run(() => httpClient.delete(`/operators/${o.id}`))}
                  >
                    Удалить
                  </button>
                </span>
              </div>
              {code?.id === o.id && anyBot && (
                <div className="op-code" aria-live="polite">
                  Откройте{' '}
                  {bots.map((b, i) => (
                    <span key={b.platform}>
                      {i > 0 && ' или '}
                      <a
                        href={b.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="v2-link"
                      >
                        @{b.username} ↗
                      </a>
                    </span>
                  ))}{' '}
                  с аккаунта оператора и отправьте код:
                  <b>{code.value}</b>
                </div>
              )}
            </div>
          ))}
          <div className="op-row">
            <input
              className="st-input"
              placeholder="Имя оператора, например Анна"
              value={name}
              maxLength={60}
              onChange={(e) => setName(e.target.value)}
            />
            <button
              type="button"
              className="v2-btn"
              disabled={busy || !name.trim()}
              onClick={() =>
                run(async () => {
                  await httpClient.post('/operators', { name: name.trim() });
                  setName('');
                })
              }
            >
              Добавить
            </button>
          </div>
          <p className="op-hint">
            Запросы получают операторы на смене — в кабинете и в боте. В боте смену включают
            командами /online и /offline.
          </p>
        </section>

        <section className="v2-card op-card">
          <h2 className="v2-h2">Где клиенты могут позвать оператора</h2>
          {data.assistants.map((a) => (
            <div key={a.id} className="op-row">
              <span>{a.name}</span>
              <button
                type="button"
                role="switch"
                aria-checked={a.enabled}
                aria-label={`Оператор у ассистента ${a.name}`}
                disabled={busy}
                className={a.enabled ? 'st-switch is-on' : 'st-switch'}
                onClick={() =>
                  run(() =>
                    httpClient.put(`/operators/assistants/${a.id}`, { enabled: !a.enabled }),
                  )
                }
              >
                <span />
              </button>
            </div>
          ))}
          <p className="op-hint">
            Клиент пишет «оператор», «позовите менеджера» или присылает файл — запрос уходит
            операторам на смене, ассистент молчит, пока оператор не завершит разговор.
          </p>
        </section>
      </div>
    </>
  );
}
